"use client"

import { useEffect, useRef, useState } from "react"

interface UseTextExpansionReturn {
  isExpanded: boolean
  setIsExpanded: (value: boolean) => void
  showSeeMore: boolean
  visibleText: string
  hiddenText: string
  descriptionRef: React.RefObject<HTMLParagraphElement | null>
  measureRef: React.RefObject<HTMLParagraphElement | null>
}

export const useTextExpansion = (text: string, maxLines: number = 8): UseTextExpansionReturn => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showSeeMore, setShowSeeMore] = useState(false)
  const [visibleText, setVisibleText] = useState(text)
  const [hiddenText, setHiddenText] = useState("")
  const descriptionRef = useRef<HTMLParagraphElement | null>(null)
  const measureRef = useRef<HTMLParagraphElement | null>(null)

  useEffect(() => {
    // Reset state khi text thay đổi
    setIsExpanded(false)
    setShowSeeMore(false)
    setVisibleText(text)
    setHiddenText("")

    // Hàm tính toán vị trí cắt text để fit vào maxLines
    const calculateTextSplit = () => {
      if (!measureRef.current || !descriptionRef.current) return

      // Lấy width từ element hiển thị
      const visibleWidth = descriptionRef.current.offsetWidth
      if (visibleWidth === 0) {
        // Nếu width = 0, element chưa render xong, thử lại sau
        requestAnimationFrame(calculateTextSplit)
        return
      }

      // Đảm bảo element đo có cùng width với element hiển thị
      measureRef.current.style.width = `${visibleWidth}px`

      // Đợi browser render xong
      requestAnimationFrame(() => {
        if (!measureRef.current) return

        const styles = getComputedStyle(measureRef.current)
        const fontSize = parseFloat(styles.fontSize) || 16
        const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5 || fontSize * 1.5
        const maxHeight = lineHeight * maxLines

        // Đo chiều cao của toàn bộ text
        measureRef.current.textContent = text
        const fullHeight = measureRef.current.scrollHeight

        // Nếu text ngắn hơn maxLines, không cần cắt
        if (fullHeight <= maxHeight + 0.5) {
          setShowSeeMore(false)
          setVisibleText(text)
          setHiddenText("")
          return
        }

        // Tìm vị trí cắt text bằng binary search
        let left = 0
        let right = text.length
        let bestSplit = 0

        while (left <= right) {
          const mid = Math.floor((left + right) / 2)
          const testText = text.substring(0, mid)

          measureRef.current.textContent = testText
          const testHeight = measureRef.current.scrollHeight

          if (testHeight <= maxHeight + 0.5) {
            // Text này vẫn fit, có thể cắt sâu hơn
            bestSplit = mid
            left = mid + 1
          } else {
            // Text này quá dài, cần cắt ngắn hơn
            right = mid - 1
          }
        }

        // Tìm vị trí cắt tốt nhất (tại khoảng trắng gần nhất để tránh cắt giữa từ)
        let finalSplit = bestSplit
        if (finalSplit < text.length) {
          // Tìm khoảng trắng gần nhất về phía trước
          const spaceIndex = text.lastIndexOf(" ", finalSplit)
          if (spaceIndex > finalSplit * 0.7) {
            // Chỉ dùng space nếu nó không quá xa
            finalSplit = spaceIndex
          }
        }

        // Kiểm tra lại với "... See more" được thêm vào
        // Đảm bảo "... See more" luôn nằm trong dòng cuối cùng (không wrap xuống dòng mới)
        // Thêm space ở đầu để match với render (ml-1 tương đương 1 space)
        const seeMoreText = " ... See more"

        // Tìm vị trí cắt tốt nhất để "... See more" fit trong cùng dòng cuối
        let adjustedSplit = finalSplit
        let optimalSplit = finalSplit
        let bestScore = -1 // Điểm số để đánh giá vị trí tốt nhất (số dòng không tăng = tốt nhất)
        let attempts = 0
        const maxAttempts = text.length // Tránh infinite loop

        while (adjustedSplit > 0 && attempts < maxAttempts) {
          attempts++

          // Đo text không có "See more"
          const textWithoutSeeMore = text.substring(0, adjustedSplit)
          measureRef.current.textContent = textWithoutSeeMore
          const heightWithoutSeeMore = measureRef.current.scrollHeight
          const linesWithoutSeeMore = Math.ceil(heightWithoutSeeMore / lineHeight)

          // Đo text có "See more"
          const textWithSeeMore = textWithoutSeeMore + seeMoreText
          measureRef.current.textContent = textWithSeeMore
          const heightWithSeeMore = measureRef.current.scrollHeight
          const linesWithSeeMore = Math.ceil(heightWithSeeMore / lineHeight)

          // Kiểm tra điều kiện cơ bản: phải fit trong maxLines
          if (heightWithSeeMore <= maxHeight + 0.5 && linesWithSeeMore <= maxLines) {
            // Tính điểm số: nếu số dòng không tăng = điểm cao nhất
            const lineDiff = linesWithSeeMore - linesWithoutSeeMore
            const score = lineDiff === 0 ? 1000 : 1000 - lineDiff * 100 - adjustedSplit

            // Ưu tiên vị trí có số dòng không tăng (See more nằm trong cùng dòng cuối)
            if (lineDiff === 0) {
              optimalSplit = adjustedSplit
              bestScore = score
              break // Tìm được vị trí tốt nhất, dừng lại
            }

            // Nếu chưa tìm được vị trí tốt nhất, lưu lại vị trí tốt nhất hiện tại
            if (score > bestScore) {
              optimalSplit = adjustedSplit
              bestScore = score
            }
          }

          // Nếu vượt quá, cần cắt ngắn hơn
          adjustedSplit--

          // Tìm khoảng trắng gần nhất để tránh cắt giữa từ
          const spaceIndex = text.lastIndexOf(" ", adjustedSplit)
          if (spaceIndex > 0 && spaceIndex > adjustedSplit * 0.7) {
            adjustedSplit = spaceIndex
          }
        }

        finalSplit = optimalSplit

        setShowSeeMore(true)
        setVisibleText(text.substring(0, finalSplit))
        setHiddenText(text.substring(finalSplit))
      })
    }

    // Kiểm tra sau một chút delay để đảm bảo DOM đã render
    const timeoutId = setTimeout(() => {
      calculateTextSplit()
    }, 100)

    // Lắng nghe sự kiện resize
    window.addEventListener("resize", calculateTextSplit)

    // Sử dụng ResizeObserver để theo dõi thay đổi kích thước của element
    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== "undefined" && descriptionRef.current) {
      resizeObserver = new ResizeObserver(() => {
        calculateTextSplit()
      })
      resizeObserver.observe(descriptionRef.current)
    }

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", calculateTextSplit)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [text, maxLines])

  return {
    isExpanded,
    setIsExpanded,
    showSeeMore,
    visibleText,
    hiddenText,
    descriptionRef,
    measureRef,
  }
}
